import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form';
import ReactMDE from 'redux-forms-markdown-editor';

import { required, url } from '../utilities/validation';
import { renderField, renderFileField } from '../utilities/form';
import { toFloat } from '../utilities/normalisation';
import { storage } from '../firebase';

import { v4 as uuidv4 } from 'uuid';

const SecondHandBoardgameForm = ({ title, handleSubmit, invalid, submitting, change, ...props }) => {
  const [ isUploading, setUploading ] = useState(false);

  const handleFile = (field) => {
    return (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        setUploading(true);
        const file = files[0];
        const ext = file.name.split('.').pop();
        const imageRef = storage.ref().child(`images/${uuidv4()}.${ext}`);
        imageRef.put(file)
          .then(snapshot => snapshot.ref.getDownloadURL())
          .then(url => {
            change(field, url);
            setUploading(false);
          });
      }
    };
  };

  const mdConfig = {
    bold: true,
    italic: true,
    heading: true,
    orderedList: true,
    unorderedList: true,
    blockQuote: false,
    html: false,
    url: true,
    image: true,
    youtube: true,
    canPreview: false,
  };

  const mdButtonStyle = {
    borderRadius: '4px',    
  };

  const mdTextAreaStyle = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundColor: '#edf2f7',
    border: '#edf2f7',
    padding: '.75rem 1rem .75rem 1rem',
    borderRadius: '.25rem',
  };

  const languages = [
    { id: 'Italiano', name: 'Italiano' },
    { id: 'Inglese', name: 'Inglese' },
    { id: 'Tedesco', name: 'Tedesco' },
  ];

  return (
    <React.Fragment>
      <div className="flex flex-wrap justify-center">
        <div className="w-full max-w-lg relative">
          <h1 className="font-black font-serif mb-8 text-3xl tracking-tight block text-black">{ title }</h1>
        </div>
        <form className="w-full max-w-lg" onSubmit={handleSubmit}>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="title">
                Titolo
              </label>
              <Field
                id="title"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                name="title"
                type="text"
                component={renderField}
                placeholder="e.g. 7 Wonders"
                disabled={props.loading}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="description">
                Descrizione
              </label>
              <Field
                id="description"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                name="description"
                component={ReactMDE}
                buttonConfig={mdConfig}
                buttonStyle={mdButtonStyle}
                textAreaStyle={mdTextAreaStyle}
                placeholder="e.g. Un gioco straordinario di deduzione..."
                disabled={props.loading}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="edition">
                Edizione
              </label>
              <Field
                id="edition"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                name="edition"
                type="text"
                component={renderField}
                placeholder="e.g. Giochi Uniti"
                disabled={props.loading}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-2/3 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="language">
                Lingua
              </label>
              <div className="relative">
                <Field
                  id="language"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="language"
                  type="select"
                  multiple={false}
                  component={renderField}
                  disabled={props.loading}>
                    { languages.map(language => <option key={language.id} value={language.id}>{language.name}</option>) }
                </Field>
              </div>
            </div>
            <div className="w-1/3 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="languageDependant">
                Dipendete dalla lingua
              </label>
              <div className="relative">
                <Field
                  id="languageDependant"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="languageDependant"
                  type="select"
                  multiple={false}
                  component={renderField}
                  disabled={props.loading}>
                    <option value={false}>No</option>
                    <option value={true}>Sì</option>
                </Field>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="image">
                Immagine
              </label>
              <div className="relative">
                <Field
                  id="image"
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded rounded-r-none py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                  name="image"
                  type="text"
                  component={renderFileField}
                  handleFile={handleFile}
                  placeholder="e.g. https://site.com/image.png"
                  disabled={props.loading || isUploading}
                  validate={[required, url]} />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="price">
                Prezzo
              </label>
              <Field
                id="price"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                name="price"
                type="number"
                min={0}
                component={renderField}
                placeholder="e.g. 20"
                disabled={props.loading}
                normalize={toFloat}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap justify-center">
            <button disabled={invalid || submitting || isUploading} className={`w-full md:w-auto bg-blue-700 text-white font-bold py-4 px-4 rounded ${invalid || submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-800'}`} type="submit">
              Salva
            </button>
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

SecondHandBoardgameForm.propTypes = {
  title: PropTypes.string,
};

export default reduxForm({
  form: 'secondHandBoardgame',
})(SecondHandBoardgameForm);